import styled from 'styled-components';
import { Scrollbar } from '@ann/web-components';

export const Container = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;

  .add-button {
    height: 40px;
    border-radius: 8px !important;
  }
`;

export const SearchFieldWrap = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
  width: 100%;
`;

export const InputArea = styled.div`
  display: flex;
  gap: 4px;
  width: 100%;
  box-sizing: border-box;
  border: 1px solid
    ${(props) =>
      props.$isActive
        ? props.theme.colors.clearBlue
        : props.theme.colors.silverGrayTwo};
  border-radius: 8px;
  background: ${(props) => props.theme.colors.white};
  cursor: text;
  transition: border-color 0.15s ease;

  /* Una sola línea (sin tags): altura fija 40px y contenido centrado — Figma */
  ${(props) =>
    props.$compact
      ? `
    align-items: center;
    min-height: 40px;
    height: 40px;
    padding: 0 16px;
  `
      : `
    align-items: flex-start;
    min-height: 40px;
    padding: 4px 16px;
  `}

  &:focus-within {
    border-color: ${(props) => props.theme.colors.clearBlue};
  }
`;

export const SearchIconWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  pointer-events: none;

  ${(props) =>
    props.$expandable &&
    `
    align-self: flex-start;
    margin-top: 4px;
  `}
`;

/** Inset 8px a la derecha para que el track del Scrollbar no pegue al borde del input */
export const TagsScrollOuter = styled.div`
  box-sizing: border-box;
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: ${(props) => (props.$expandable ? 'stretch' : 'center')};
  min-height: ${(props) => (props.$expandable ? '32px' : '0')};
  max-height: ${(props) => (props.$expandable ? '150px' : '40px')};
  padding-right: 8px;

  & > * {
    max-height: inherit;
    width: 100%;
  }
`;

/** Scrollbar del input (tags + texto); el inset derecho lo da TagsScrollOuter */
export const TagsInputScrollbar = styled(Scrollbar).attrs({
  size: 'small',
  autoHide: 'false',
  display: 'flex',
  padding: '0',
})`
  width: 100%;
  flex: 1 1 auto;
  min-height: 0;
  max-height: ${(props) => (props.$expandable ? '150px' : '40px')};
`;

export const TagsAndInputInner = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 0 4px 0 0;
  width: 100%;
  box-sizing: border-box;
  min-height: ${(props) => (props.$compact ? '20px' : '32px')};
`;

export const NativeInput = styled.input`
  flex: 1 1 120px;
  min-width: 80px;
  margin: 0;
  border: none;
  outline: none;
  background: transparent;
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: ${(props) => props.theme.colors.davysGrey};

  padding: 0;
  min-height: 20px;

  &::placeholder {
    color: ${(props) => props.theme.colors.davysGrey};
    opacity: 0.7;
  }
`;

export const DropdownPanel = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% + 4px);
  z-index: 10;
  max-height: min(280px, 55vh);
  border-radius: 8px;
  background: ${(props) => props.theme.colors.white};
  box-shadow: 8px 8px 16px 0 rgba(0, 0, 0, 0.08);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  visibility: ${(props) => (props.$isOpen ? 'visible' : 'hidden')};
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  pointer-events: ${(props) => (props.$isOpen ? 'auto' : 'none')};
  transform: ${(props) => (props.$isOpen ? 'translateY(0)' : 'translateY(-4px)')};
  transition:
    opacity 0.15s ease,
    visibility 0.15s ease,
    transform 0.15s ease;
`;

/**
 * Contenedor del listado: limita alto para el Scrollbar.
 * padding-right: 8px para que el track quede a 8px del borde del panel (sin calc en el hijo).
 */
export const DropdownScrollInner = styled.div`
  box-sizing: border-box;
  width: 100%;
  max-height: min(260px, 50vh);
  min-height: 120px;
  padding-right: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

/**
 * Scrollbar: attrs ponen display:flex sin direction → eje horizontal y el hijo encoge al contenido
 * (hover solo hasta el texto). Mismo patrón que AgentsList: column + stretch.
 */
export const DropdownScrollbar = styled(Scrollbar).attrs({
  size: 'small',
  autoHide: 'false',
  display: 'flex',
  padding: '0',
})`
  flex-direction: column;
  align-items: stretch;
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
  max-height: min(260px, 50vh);
  overscroll-behavior: contain;
`;

export const DropdownList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  flex-shrink: 0;
  min-width: 0;
  min-height: min-content;
  box-sizing: border-box;
`;

/** Contenedor siempre en DOM; oculta filas de resultados mientras carga o en estado vacío */
export const ResultsBlock = styled.div`
  display: ${(props) => (props.$visible ? 'contents' : 'none')};
`;

export const DepartmentSectionHeader = styled.div`
  display: ${(props) => (props.$visible ? 'flex' : 'none')};
  align-items: center;
  padding: 8px 16px;
  width: 100%;
  box-sizing: border-box;
`;

export const DropdownItemButton = styled.button`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  min-width: 0;
  padding: 8px 16px;
  margin: 0;
  border: none;
  background: ${(props) => props.theme.colors.white};
  cursor: pointer;
  text-align: left;
  box-sizing: border-box;

  &:hover {
    background: ${(props) => props.theme.colors.highClearGray};
  }

  &:focus-visible {
    outline: 2px solid ${(props) => props.theme.colors.clearBlue};
    outline-offset: -2px;
  }
`;

export const DropdownItemText = styled.div`
  flex: 1;
  min-width: 0;
`;

export const LoadingBlock = styled.div`
  display: ${(props) => (props.$visible ? 'flex' : 'none')};
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  box-sizing: border-box;
  padding: 12px 16px;
`;

export const EmptyState = styled.div`
  display: ${(props) => (props.$visible ? 'block' : 'none')};
  padding: 16px;
`;

export const EmptyStateText = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${(props) => props.theme.colors.davysGrey};
`;

export const AvatarWrap = styled.div`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  overflow: hidden;
  border-radius: 50%;

  & > * {
    width: 24px !important;
    height: 24px !important;
  }
`;
